import { LanguageService } from './../service/language.service';

import { throwError as observableThrowError, Observable, BehaviorSubject, of } from 'rxjs';

import { zip, tap, catchError, switchMap, take, filter, finalize, shareReplay } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';

import { AutenticacaoService } from '../service/autenticacao.service';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
  HttpEvent,
  HttpResponse
} from '@angular/common/http';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { StorageProxy } from '../service/storage-proxy.service';
import { ConfigProvider } from '../../../common/providers/config.provider';
import { Credential } from "../../autenticacao/model/credential";

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {

  /**
   * Mantive as propriedades para minimizar impacto.
   * A lógica de concorrência foi corrigida usando um refresh "compartilhado".
   */
  isRefreshingToken: boolean = false;

  /**
   * IMPORTANTE:
   * Era "" e isso fazia as requests concorrentes pegarem token vazio imediatamente.
   * Agora começa null e só libera quando token real chega.
   */
  tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  /**
   * Observable do refresh em andamento (compartilhado).
   * Assim: 1 refresh por vez, e as requests concorrentes aguardam o mesmo.
   */
  private refreshInFlight$?: Observable<string>;

  counter: number = 0;

  @BlockUI()
  blockUI: NgBlockUI;

  constructor(private injector: Injector) {}

  private get authService(): AutenticacaoService {
    return this.injector.get(AutenticacaoService);
  }

  private get configProvider(): ConfigProvider {
    return this.injector.get(ConfigProvider);
  }

  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  private get router(): Router {
    return this.injector.get(Router);
  }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const appConfig = this.configProvider.getConfig();
    const linguagem = StorageProxy.getString('linguagem');

    // -------------------------
    // 1) Normalização de URL
    // -------------------------
    if (req.url.startsWith('/api/') || req.url.startsWith('/connect/token')) {

      if (req.url.startsWith('/api/')) {
        let language: string = (linguagem === "pt") ? "pt-BR" : (linguagem || '');

        if (!language) {
          language = 'pt';
        }

        req = req.clone({ url: `${appConfig.serverUrl}/${language}${req.url}` });

      } else {
        req = req.clone({ url: `${appConfig.serverUrl}${req.url}` });
      }
    }

    // -------------------------
    // 2) Cookies / headers
    // -------------------------
    if (this.authService.isAutenticadoSemToken()) {
      req = req.clone({ withCredentials: true });
    }

    // Accept-Language ok
    req = req.clone({ headers: req.headers.set('Accept-Language', linguagem) });

    // OBS: Esse header não deve ser setado no client (é header de RESPOSTA do servidor)
    // Mantive comentado para não "quebrar" o build, mas recomendo remover.
    // req = req.clone({ headers: req.headers.set('Access-Control-Allow-Origin', '*') });

    // -------------------------
    // 3) Loading (garante 1 stop por request usando finalize)
    // Quando a request tem header 'blockLoading', não exibe o loading global.
    // -------------------------
    this.counter++;
    const shouldBlock =
      req.headers.keys().length <= 0 ||
      req.headers.keys().some((s: any) => s === 'blockLoading') === false;

    if (shouldBlock && this.counter == 1) {
      this.blockUI.start();
    }

    const shouldSkipAuth =
      req.url.indexOf(appConfig.serverUrl) < 0 ||
      !this.authService.obterTokenAutenticado() ||
      req.url.indexOf('/connect/token') >= 0 ||
      (req.url.indexOf('/provider/') >= 0 && req.url.indexOf('/campanhas') >= 0);

    const requestToSend = shouldSkipAuth
      ? req
      : this.addToken(req, this.authService.obterTokenAutenticado() || '');
      
    return next.handle(requestToSend).pipe(
      tap(this.handleResponse.bind(this)),
      catchError((e: any) => {
        // Para requests sem token (login/refresh etc), mantém o comportamento antigo:
        if (shouldSkipAuth) {
          if (e instanceof HttpErrorResponse && e.status === 401) {
            localStorage.removeItem('currentUser');
            this.router.navigate(['/login']);
          }
          return observableThrowError(e);
        }

        // Para requests autenticadas, passa pelo handler (401 -> refresh)
        return this.errorHandler(e, req, next);
      }),
      // garante que o contador sempre será decrementado (inclusive em retry)
      finalize(() => this.handleLoading())
    );
  }

  private handleResponse(ev: HttpEvent<any>) {
    if (false === ev instanceof HttpResponse) {
      return;
    }
    // Não chama loading aqui para não "duplar" decrement.
    // O loading é controlado no finalize do intercept.
  }

  private handleLoading() {
    this.counter--;
    // proteção contra counter negativo (pode acontecer em edge cases de retry/cancel)
    if (this.counter <= 0) {
      this.counter = 0;
      this.blockUI.stop();
    }
  }

  errorHandler(error: Error, req: HttpRequest<any>, next: HttpHandler) {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 400:
          return this.handle400Error(error);
        case 401:
          return this.handle401Error(req, next);
        case 403:
          return this.handle403Error(error);
        case 500:
        case 0:
          return this.handle500Error(req.method, error);
        default:
          return observableThrowError(error);
      }
    } else {
      return observableThrowError(error);
    }
  }

  handle403Error(error: HttpErrorResponse) {
    if (error.url && error.url.indexOf('logout') > 0) {
      this.router.navigate(['/login']);
      return this.logoutUser();
    }
    else {
      this.router.navigate(["/app/acesso-negado"], {
        queryParams: { rota: error.url }
      });
      return observableThrowError(error);
    }
  }

  handle400Error(error: HttpErrorResponse) {
    if (
      error &&
      error.status === 400 &&
      (error as any).error &&
      (error as any).error.error === 'invalid_grant'
    ) {
      return this.logoutUser();
    }

    return observableThrowError(error);
  }

  /**
   * Corrigido para concorrência:
   * - 1 refresh por vez (refreshInFlight$ + shareReplay)
   * - Sem subscribe() dentro do interceptor
   * - tokenSubject começa com null e só libera quando token real existir
   */
  handle401Error(req: HttpRequest<any>, next: HttpHandler) {

    // Se não tem refresh em andamento, cria um (compartilhado)
    if (!this.refreshInFlight$) {
      this.isRefreshingToken = true;

      // trava o subject (pra ninguém pegar token antigo/vazio)
      this.tokenSubject.next(null);

      this.refreshInFlight$ = this.authService.refreshToken().pipe(
        switchMap((credential: Credential) => {
          // Ajuste aqui caso seu model use outro nome:
          const token = (credential as Credential)?.accessToken;

          if (token && String(token).trim().length > 0) {
            this.tokenSubject.next(token);
            return of(token);
          }

          return this.logoutUser();
        }),
        // compartilha o MESMO refresh com todas as requisições concorrentes
        shareReplay(1),
        catchError((_) => this.logoutUser()),
        finalize(() => {
          this.isRefreshingToken = false;
          this.refreshInFlight$ = undefined;
        })
      );
    }

    // Todas as requisições (incluindo a que iniciou o refresh) aguardam o mesmo refresh
    return this.refreshInFlight$.pipe(
      switchMap((token: string) => next.handle(this.addToken(req, token)))
    );
  }

  handle500Error(method: string, error: any) {
    const mainMessage =
      method === 'GET'
        ? 'errosPadrao.erroRequisicaoGet'
        : 'errosPadrao.erroRequisicao';

    this.translateService
      .get('errosPadrao.erroRequisicaoTitulo').pipe(
        zip(this.translateService.get(mainMessage)))
      .subscribe((data) => swal.fire(data[0], data[1], 'error'));

    return observableThrowError(error);
  }

  logoutUser() {
    this.authService.logoutUser();
    this.router.navigate(['/login']);
    return observableThrowError('');
  }
}
